import React from "react";
import {
  CalendarDays,
  History,
  LoaderCircle,
  RefreshCcw,
} from "lucide-react";
import {
  buildOrderImportCaptureKey,
  DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME,
  DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import useDispatchCaptureArrivalHighlight from "../hooks/useDispatchCaptureArrivalHighlight.js";
import { fetchSollioUniagOrderImportHistory } from "../lib/systemConfigApi.js";
import {
  formatCaptureActivityGroupLabel,
  groupCaptureItemsByActivityDate,
} from "../utils/dispatchCaptureListGroups.js";
import { MetricCard } from "./ResultsPrimitives.jsx";
import { joinClasses, statusToneClasses } from "./resultsPrimitives/shared.js";

const PAGE_SIZE = 25;
const SECONDARY_BUTTON_CLASS_NAME = "inline-flex min-h-9 items-center justify-center gap-2 rounded-[4px] border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";
const ACTIVITY_TIME_ZONE = "America/Toronto";

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("fr-CA", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: ACTIVITY_TIME_ZONE,
});
const TIME_FORMATTER = new Intl.DateTimeFormat("fr-CA", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ACTIVITY_TIME_ZONE,
});

function normalizeHistoryPayload(payload, fallbackPage) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const page = Number.isInteger(Number(payload?.page)) && Number(payload.page) > 0
    ? Number(payload.page)
    : fallbackPage;
  const pageSize = Number.isInteger(Number(payload?.page_size)) && Number(payload.page_size) > 0
    ? Number(payload.page_size)
    : PAGE_SIZE;
  const total = Number.isFinite(Number(payload?.total)) && Number(payload.total) >= 0
    ? Math.trunc(Number(payload.total))
    : items.length;
  return {
    hasMore: payload?.has_more === true,
    items,
    page,
    pageSize,
    total,
  };
}

export function formatTimestamp(value) {
  const parsedDate = new Date(value || "");
  if (Number.isNaN(parsedDate.getTime())) {
    return {
      dateTimeLabel: "Date et heure non disponibles",
      isoValue: undefined,
      timeLabel: "Heure non disponible",
    };
  }
  return {
    dateTimeLabel: DATE_TIME_FORMATTER.format(parsedDate),
    isoValue: parsedDate.toISOString(),
    timeLabel: TIME_FORMATTER.format(parsedDate),
  };
}

function formatImportMode(action) {
  if (action === "import_replace") {
    return "Remplacement";
  }
  if (action === "import_merge") {
    return "Fusion";
  }
  return "Non disponible";
}

function formatOrderCount(value) {
  if (value == null || value === "") {
    return "Non disponible";
  }
  const count = Number(value);
  return Number.isInteger(count) && count >= 0
    ? count.toLocaleString("fr-CA")
    : "Non disponible";
}

export function resolveActorLabel(item) {
  const email = typeof item?.actor_email === "string" ? item.actor_email.trim() : "";
  const userId = typeof item?.actor_user_id === "string" ? item.actor_user_id.trim() : "";
  return email || userId || "Utilisateur non disponible";
}

function resolveImpactPresentation(level) {
  if (level === "breaking") {
    return {
      label: "Dispatch à revalider",
      metricBorderClassName: "border-[var(--danger)]!",
      tone: "danger",
    };
  }
  if (level === "minor") {
    return {
      label: "Impact mineur",
      metricBorderClassName: "border-[var(--color-border-warning-muted)]!",
      tone: "warning",
    };
  }
  if (level === "none") {
    return {
      label: "Sans impact",
      metricBorderClassName: "border-accent!",
      tone: "success",
    };
  }
  return {
    label: "Impact non précisé",
    metricBorderClassName: "border-slate-800!",
    tone: "neutral",
  };
}

export function ImportContextItem({ detail = "", label, value, wrap = false }) {
  return (
    <div className="grid min-w-0 gap-0 rounded-[4px] border border-slate-200 bg-slate-50/80 px-2 py-1">
      <span className="text-[0.52rem] font-extrabold uppercase tracking-[0.07em] text-slate-500">{label}</span>
      <strong
        className={joinClasses([
          "min-w-0 text-[0.76rem] leading-tight text-slate-950",
          wrap ? "whitespace-normal break-words" : "truncate",
        ])}
        title={value}
      >
        {value}
      </strong>
      {detail ? (
        <span className="min-w-0 whitespace-pre-line break-words text-[0.6rem] leading-4 text-slate-500">
          {detail}
        </span>
      ) : null}
    </div>
  );
}

function ImportOrderMetric({ emphasized = false, label, metricBorderClassName = "", value }) {
  return (
    <MetricCard
      className={joinClasses([
        "gap-0! rounded-[4px]! px-2! py-1! text-center!",
        emphasized ? `border-2! ${metricBorderClassName}` : "",
      ])}
      label={label}
      labelClassName="text-[0.52rem]! tracking-[0.05em] leading-none"
      tone="neutral"
      value={value}
      valueClassName="text-[0.92rem]! leading-tight! tabular-nums"
    />
  );
}

export function OrderImportHistoryCard({ item, isNew = false }) {
  const timestamp = formatTimestamp(item?.created_at);
  const impact = resolveImpactPresentation(item?.impact_level);
  const actorLabel = resolveActorLabel(item);
  const sourceFilename = typeof item?.source_filename === "string" && item.source_filename.trim()
    ? item.source_filename.trim()
    : "Fichier non disponible";
  const summary = typeof item?.summary === "string" ? item.summary.trim() : "";
  const impactSummary = typeof item?.impact_summary === "string" ? item.impact_summary.trim() : "";
  const providerLabel = item?.provider ? `Import ${item.provider}` : "Import commandes";
  const visibleImpactSummary = impactSummary && impactSummary !== summary ? impactSummary : "";

  return (
    <article
      className={joinClasses([
        "grid min-w-0 gap-1.5 rounded-[4px] border border-black/6 bg-white px-2.5 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
        isNew ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME : "",
      ])}
      data-dispatch-capture-arrival={isNew ? "true" : undefined}
      data-sollio-uniag-import-card="true"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <strong className="shrink-0 text-[0.86rem] tracking-[-0.02em] text-slate-950">
            {providerLabel}
          </strong>
          <p className="m-0 flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 gap-y-0 text-[0.62rem] font-medium leading-4 text-slate-500">
            <span className="inline-flex min-w-0 items-baseline gap-1.5">
              <span className="text-slate-400">Importé</span>
              <time
                className="whitespace-nowrap text-[0.92rem] font-bold leading-none tabular-nums text-slate-950"
                dateTime={timestamp.isoValue}
                title={timestamp.dateTimeLabel}
              >
                {timestamp.timeLabel}
              </time>
            </span>
          </p>
        </div>
        <span
          className={joinClasses([
            "inline-flex shrink-0 items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.06em]",
            statusToneClasses[impact.tone],
          ])}
        >
          {impact.label}
        </span>
      </div>

      <div
        className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(7rem,0.8fr)_minmax(7rem,0.8fr)]"
        data-sollio-uniag-import-details-row="true"
      >
        <ImportContextItem detail={summary} label="Fichier" value={sourceFilename} wrap />
        <ImportContextItem label="Utilisateur" value={actorLabel} wrap />
        <ImportContextItem
          detail={visibleImpactSummary}
          label="Mode d’import"
          value={formatImportMode(item?.action)}
          wrap
        />
        <ImportOrderMetric
          emphasized
          label="Commandes totales"
          metricBorderClassName={impact.metricBorderClassName}
          value={formatOrderCount(item?.total_order_count)}
        />
        <ImportOrderMetric
          label="Nouvelles commandes"
          value={formatOrderCount(item?.new_order_count)}
        />
      </div>
    </article>
  );
}

export default function SollioUniagOrderImportHistory() {
  const [page, setPage] = React.useState(1);
  const [historyState, setHistoryState] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const requestSequenceRef = React.useRef(0);

  const loadHistory = React.useCallback(async ({ quiet = false } = {}) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    if (!quiet) {
      setIsLoading(true);
      setLoadError("");
    }
    try {
      const payload = normalizeHistoryPayload(
        await fetchSollioUniagOrderImportHistory({ page, pageSize: PAGE_SIZE }),
        page,
      );
      if (requestSequence === requestSequenceRef.current) {
        setHistoryState(payload);
        setLoadError("");
      }
    } catch (error) {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setLoadError(error instanceof Error ? error.message : "Impossible de charger l’historique des imports de commandes.");
      }
    } finally {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setIsLoading(false);
      }
    }
  }, [page]);

  React.useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    let intervalId = null;
    const refreshQuietly = () => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        void loadHistory({ quiet: true });
      }
    };
    const startPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        intervalId = window.setInterval(refreshQuietly, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        if (intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      refreshQuietly();
      startPollingInterval();
    };

    startPollingInterval();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [loadHistory]);

  const items = historyState?.items || [];
  const importKeys = React.useMemo(() => items.map(buildOrderImportCaptureKey), [items]);
  const highlightedKeys = useDispatchCaptureArrivalHighlight(importKeys, {
    enabled: historyState != null && historyState.page === page,
    resetKey: historyState?.page,
  });
  const total = historyState?.total || 0;
  const currentPage = historyState?.page || page;
  const pageSize = historyState?.pageSize || PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const groupedItems = React.useMemo(
    () => groupCaptureItemsByActivityDate(items, (item) => item?.created_at),
    [items],
  );

  return (
    <div className="grid min-w-0 gap-3" data-sollio-uniag-import-history="true">
      <section className="grid gap-2 rounded-[4px] border border-black/6 bg-white px-4 py-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
            statusToneClasses.success,
          ])}>
            Lecture seule
          </span>
          <span className="text-[0.8rem] font-medium text-slate-600">
            {total} import{total > 1 ? "s" : ""} dans l’historique
          </span>
        </div>
        <div className="grid gap-0.5 rounded-[4px] border border-slate-200 bg-slate-50/70 px-3 py-2">
          <h2 className="m-0 text-[0.86rem] font-bold text-slate-950">Imports commandes Sollio / Uniag</h2>
          <p className="m-0 text-[0.72rem] leading-5 text-slate-600">
            Fichiers Sollio et Uniag importés.
          </p>
        </div>
      </section>

      {loadError ? (
        <section className="grid gap-3 rounded-[4px] border border-rose-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]" role="alert">
          <p className="m-0 text-[0.9rem] font-semibold text-rose-700">{loadError}</p>
          <div>
            <button className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => void loadHistory()} type="button">
              <RefreshCcw size={15} aria-hidden="true" />
              Réessayer
            </button>
          </div>
        </section>
      ) : isLoading && !historyState ? (
        <section className="flex min-h-52 items-center justify-center gap-3 rounded-[4px] border border-black/6 bg-white text-[0.86rem] font-medium text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />
          Chargement des imports commandes…
        </section>
      ) : items.length > 0 ? (
        <section className="grid max-w-full min-h-min min-w-0 gap-6 overflow-x-clip" aria-label="Historique des imports commandes Sollio et Uniag">
          {groupedItems.map((group) => (
            <div
              className="grid max-w-full min-h-min min-w-0 gap-2 overflow-x-clip rounded-[4px] border border-slate-200 bg-slate-50/90 p-2.5"
              data-sollio-uniag-import-date-group="true"
              key={group.activityDate || "unspecified"}
            >
              <header className="flex items-center justify-between gap-3 rounded-[4px] border border-slate-300 bg-slate-200/90 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
                <div className="flex min-w-0 items-center gap-2 text-[0.95rem] font-extrabold tracking-[-0.02em] text-slate-950">
                  <CalendarDays className="shrink-0 text-slate-700" size={16} aria-hidden="true" />
                  <h3 className="m-0 truncate">{formatCaptureActivityGroupLabel(group.activityDate)}</h3>
                </div>
                <span className="shrink-0 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-[0.68rem] font-bold text-slate-600">
                  {group.items.length} import{group.items.length > 1 ? "s" : ""}
                </span>
              </header>
              {group.items.map((item) => (
                <OrderImportHistoryCard
                  item={item}
                  isNew={highlightedKeys.has(buildOrderImportCaptureKey(item))}
                  key={item.audit_id}
                />
              ))}
            </div>
          ))}
        </section>
      ) : (
        <section className="grid min-h-52 place-items-center rounded-[4px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
          <div className="grid max-w-lg gap-2">
            <History className="mx-auto text-slate-400" size={30} aria-hidden="true" />
            <p className="m-0 text-[1rem] font-semibold text-slate-900">Aucun import commande à afficher</p>
            <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
              Seuls les fichiers Sollio et Uniag importés apparaissent ici.
            </p>
          </div>
        </section>
      )}

      {total > 0 ? (
        <nav className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]" aria-label="Pagination des imports commandes">
          <span className="text-[0.76rem] font-medium text-slate-600">
            Page {currentPage} sur {pageCount} · {total} import{total > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              className={SECONDARY_BUTTON_CLASS_NAME}
              disabled={isLoading || currentPage <= 1}
              onClick={() => setPage((currentValue) => Math.max(1, currentValue - 1))}
              type="button"
            >
              Précédente
            </button>
            <button
              className={SECONDARY_BUTTON_CLASS_NAME}
              disabled={isLoading || !historyState?.hasMore}
              onClick={() => setPage((currentValue) => currentValue + 1)}
              type="button"
            >
              Suivante
            </button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}
